/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLocationHistoryStack } from "../../../../hooks/locationHistoryStack";
import { debounce } from "lodash";
import { ICustomersSuppliers } from "../../../../interfaces/customersSuppliers";
import { createCustomerSupplier, listCustomersSuppliers, showCustomerSupplier, updateCustomerSupplier } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: ICustomersSuppliers = {
  id: 0,
  name: "",
  type: "both"
};

export const useCustomersSuppliers = ({ filter: filterInitial, disabledShowId, includes = [], disabledGetAll, defaultLimit }:{
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>,
  includes?: string[],
}) => {
  const { idCustomerSupplier: id } = useParams();
  const { previous } = useLocationHistoryStack()
  const [customersSuppliers, setCustomersSuppliers] = useState<ICustomersSuppliers[]>([]);
  const [customerSupplier, setCustomerSupplier] = useState<ICustomersSuppliers>(DEFAULT_DATA);
  const { filter, handleFilter, sort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const navigate = useNavigate();
  const pathNew = `/financial/customers_suppliers/new`;
  const pathBack = `/financial/customer_suppliers`;

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCustomerSupplier = (key: string, value: any, custom?: (v: any) => any) => {
    setCustomerSupplier((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const goEdit = (id: string | number) => {
    const path = `/financial/customers_suppliers/${id}`;
    navigate(path);
  };

  const goBack = () => {
    if (previous?.pathname.includes(pathNew)) {
      navigate(pathBack);
      return;
    }
    navigate(-1)
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit,
    filter,
    sort
  }: {
    page?: number;
    limit?: number;
    filter?: any
    sort?: Record<string, any>
  }) => {
    setLoading(true);
    listCustomersSuppliers({ includes: [...includes], filter,  page, limit, sort })
      .then(({ customersSuppliers: listCustomersSuppliers, limit, currentPage, totalItems }) => {
        setCustomersSuppliers(listCustomersSuppliers);
        setPage(currentPage);
        setLimit(limit);
        setTotalItems(totalItems)
      })
      .finally(() => setLoading(false));
  }, 300), []);

  const search = useEntitySearch({
    list: listCustomersSuppliers,
    key: "customersSuppliers",
    includes,
    filter,
  });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showCustomerSupplier({ id,includes: []})
      .then(({ customerSupplier: CustomersSuppliersShow }) => {
        setCustomerSupplier(CustomersSuppliersShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createCustomerSupplier({ customerSupplier })
      .then(({ customerSupplier: customerSupplierCreate }) => {
        toast.success(`Cliente/Fornecedor criado com sucesso!`);
        goEdit(customerSupplierCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateCustomerSupplier({ id: customerSupplier.id!, customerSupplier })
      .then(({ customerSupplier: customerSupplierUpdate }) => {
        toast.success(`Cliente/Fornecedor atualizado com sucesso!`);
        goEdit(customerSupplierUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  const goAdd = () => {
    navigate(pathNew);
  };


  useEffect(() => {
    if (!disabledGetAll)  getAll({page, limit, filter, sort: { order: 'asc'}})
  }, [page, limit, filter])

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    customersSuppliers,
    customerSupplier,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleCustomerSupplier,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter
  };
};
